/**
 * @fileoverview Company Card Item Header Component
 * @active Used in current Next.js App Router routes
 * 
 * This component renders the header section of a company card, including:
 * - Company logo with link to company detail page
 * - Tags associated with the company
 * 
 * It is part of the current active implementation for the companies card view.
 * 
 * @see {@link src/components/companies/newCompaniesCards/companyNewCardItem/index.tsx} - Parent component
 * @see {@link src/components/companies/newCompaniesCards/index.tsx} - Card view container
 * @see {@link src/components/companies/NewCompanies.tsx} - Main active implementation
 * @see {@link src/components/companies/CompaniesWrapper.client.tsx} - Client wrapper used in App Router
 * @see {@link src/app/(main)/(home)/(research)/companies/page.tsx} - Current App Router page component
 */

import React from "react";

import { Flex } from "@chakra-ui/react";
import Link from "next/link";

import DefaultImage from "../../../ui/DefaultImage";

import CompanyTagsItem from "./CompanyTagsItem";

/**
 * Interface for the company information
 */
interface CompanyInfo {
  slug: string;
  companyName: string;
  fileLogo: string;
}

/**
 * Interface for tag information
 */
interface TagInfo {
  name: string;
  color: string;
}

/**
 * Interface for the CompanyCardItemHeader props
 */
interface CompanyCardItemHeaderProps {
  company: {
    company: CompanyInfo;
    tags?: TagInfo[];
  };
}

/**
 * Company Card Item Header component
 * 
 * Displays the company logo and tags in the header section of a company card.
 * 
 * @param props - Component props
 * @returns Rendered component
 */
const CompanyCardItemHeader: React.FC<CompanyCardItemHeaderProps> = ({ company }) => {
  return (
    <Flex
      align={"center"}
      borderBottomWidth={"1px"}
      w={"100%"}
      pb={"10px"}
      borderColor={"grey"}
      justify={"space-between"}
    >
      <Link
        href={`/companies/${company?.company?.slug}`}
        title={company?.company?.companyName}
      >
        <DefaultImage
          src={company?.company?.fileLogo}
          options={{
            alt: company?.company?.companyName,
            w: "66px",
            h: "66px",
            objectFit: "contain",
          }}
        />
      </Link>
      <Flex align={"center"} columnGap={"15px"}>
        {company?.tags?.map((tag) => (
          <CompanyTagsItem key={tag?.name + tag?.color} tag={tag} />
        ))}
      </Flex>
    </Flex>
  );
};

export default CompanyCardItemHeader;
